import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getUserReviews, addReview } from "../../services/reviews";
import { getDeliveredOrderedProductsForReview } from "../../services/orders";
import { toast } from "react-toastify";
import logoImage from "../../assets/images/logo.png";
import "../../styles/Reviews.css";

// ── Icons ────────────────────────────────────────────
const StarIcon = ({ filled }) => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill={filled ? "#E67E42" : "none"}
    stroke="#E67E42"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const ImageIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

const PlusIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="white">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
  </svg>
);

const ReplyIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 17 4 12 9 7" />
    <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
  </svg>
);

const MyReviewBadgeIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" style={{ fill: "#0e3864" }}>
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
  </svg>
);

const Spinner = ({ size = 14, color = "#E67E42" }) => (
  <span
    style={{
      display: "inline-block",
      width: size,
      height: size,
      border: `2px solid ${color}33`,
      borderTopColor: color,
      borderRadius: "50%",
      animation: "rv-spin 0.65s linear infinite",
      flexShrink: 0,
    }}
  />
);

// ── Star Rating Input ─────────────────────────────────
const StarRatingInput = ({ value, onChange }) => (
  <div className="rv-star-input">
    {[1, 2, 3, 4, 5].map((star) => (
      <button
        key={star}
        type="button"
        className={`rv-star-btn ${value >= star ? "active" : ""}`}
        onClick={() => onChange(star)}
      >
        <StarIcon filled={value >= star} />
      </button>
    ))}
    <span className="rv-star-label">
      {value} star{value !== 1 ? "s" : ""}
    </span>
  </div>
);

// ── Helpers ───────────────────────────────────────────
const normUrl = (u) =>
  typeof u === "string" ? u.replace("http://", "https://") : u;

const formatDate = (dateString) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// ── Main Component ────────────────────────────────────
export default function MyReviews() {
  const navigate = useNavigate();

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [orderedProducts, setOrderedProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [newReview, setNewReview] = useState({
    product_id: "",
    order_id: "",
    rating: 5,
    comment: "",
    review_images: [],
  });

  useEffect(() => {
    fetchReviews();
    fetchOrderedProducts();
  }, []);

  // ── Fetch delivered products available for review ─────────────
  const fetchOrderedProducts = async () => {
    try {
      setLoadingProducts(true);
      const products = await getDeliveredOrderedProductsForReview();
      setOrderedProducts(Array.isArray(products) ? products : []);
    } catch {
      toast.error("Failed to load your delivered products.");
    } finally {
      setLoadingProducts(false);
    }
  };

  // ── Fetch user's existing reviews ─────────────────────────────
  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await getUserReviews();

      // API returns { message, data: [...] } or plain array
      const raw = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];

      setReviews(
        raw.map((r) => ({
          id: r.id,
          // is_my_review is always true here (these are the user's own reviews),
          // but we store it anyway in case the endpoint ever mixes in others'.
          isMyReview: r.is_my_review !== false, // default true for this endpoint
          product_id: r.product?.id || r.product_id,
          order_id: r.order?.id || r.order_id,
          product_name: r.product?.name || r.product_name || "Product",
          product_image: normUrl(
            r.product?.product_images?.[0] ||
              r.product?.image_url ||
              r.product_image ||
              null,
          ),
          brand: r.product?.brand || r.brand || null,
          rating: Number(r.rating || 0),
          comment: r.comment || "",
          review_images: Array.isArray(r.review_images)
            ? r.review_images
                .map((img) =>
                  typeof img === "string"
                    ? normUrl(img)
                    : normUrl(img?.image_url || img?.url || null),
                )
                .filter(Boolean)
            : [],
          created_at: r.created_at,
          // Handle both `vendor_reply` and `reply` shapes from the API
          vendor_reply: (() => {
            const vr = r.vendor_reply || r.reply;
            if (!vr) return null;
            return {
              shop_name: vr.vendor?.store_name || vr.shop_name || "Vendor",
              reply: vr.reply || vr.response || vr.comment || "",
              replied_at: vr.created_at || vr.replied_at || null,
            };
          })(),
        })),
      );
    } catch {
      toast.error("Error loading reviews. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── Check if a given product+order combo was already reviewed ─
  const getProductReviewCount = (productId, orderId) =>
    reviews.filter(
      (r) =>
        String(r.product_id) === String(productId) &&
        String(r.order_id) === String(orderId),
    ).length;

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setNewReview((prev) => ({
      ...prev,
      product_id: product.id,
      order_id: product.orderId,
    }));
  };

  const handleSubmitReview = async () => {
    if (!newReview.product_id) {
      toast.warning("Please select a product to review.");
      return;
    }

    if (!newReview.order_id) {
      toast.warning("Selected delivered order is invalid.");
      return;
    }

    if (!newReview.comment.trim()) {
      toast.warning("Please write a review comment.");
      return;
    }

    // ✅ ADD THIS (important)
    const alreadyReviewed = getProductReviewCount(
      newReview.product_id,
      newReview.order_id,
    );

    if (alreadyReviewed > 0) {
      toast.info("You have already reviewed this product.");
      return;
    }

    try {
      setSubmitting(true);
      await addReview(newReview);
      await fetchReviews();

      setNewReview({
        product_id: "",
        order_id: "",
        rating: 5,
        comment: "",
        review_images: [],
      });

      setSelectedProduct(null);
      setShowAddForm(false);

      toast.success("Review submitted!");
    } catch (error) {
      // optional: still handle backend validation
      if (error?.response?.status === 409) {
        toast.info("You have already reviewed this product.");
      } else {
        toast.error("Error adding review. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setShowAddForm(false);
    setSelectedProduct(null);
    setNewReview({
      product_id: "",
      order_id: "",
      rating: 5,
      comment: "",
      review_images: [],
    });
  };

  return (
    <div className="rv-root">
      {/* ── Header ── */}
      <div className="acc-head">
        <div>
          <h1 className="acc-h1">My Reviews</h1>
          {!loading && reviews.length > 0 && (
            <p className="rv-count">
              {reviews.length} review{reviews.length !== 1 ? "s" : ""}
            </p>
          )}
        </div>

        {!loading && (
          <button
            className={`acc-btn ${showAddForm ? "acc-btn-outline" : ""}`}
            onClick={() => (showAddForm ? resetForm() : setShowAddForm(true))}
          >
            {showAddForm ? (
              "Cancel"
            ) : (
              <>
                <PlusIcon /> Write a Review
              </>
            )}
          </button>
        )}
      </div>

      {/* ── Add Review Form ── */}
      {showAddForm && (
        <div className="rv-form-card">
          <h3 className="rv-form-title">Write a New Review</h3>

          {/* Product selector */}
          <div className="rv-field">
            <label className="rv-label">
              Select a delivered product to review
            </label>

            {loadingProducts ? (
              <div className="rv-hint">
                <Spinner size={13} /> Loading products…
              </div>
            ) : orderedProducts.length === 0 ? (
              <div className="rv-empty-mini">
                <p>
                  You don't have any delivered products available for review
                  yet.
                </p>
                <button className="acc-btn" onClick={() => navigate("/")}>
                  Start Shopping
                </button>
              </div>
            ) : (
              <div className="rv-product-grid">
                {orderedProducts.map((product) => {
                  const isSelected =
                    selectedProduct?.id === product.id &&
                    selectedProduct?.orderId === product.orderId;
                  const prevCount = getProductReviewCount(
                    product.id,
                    product.orderId,
                  );

                  return (
                    <div
                      key={`${product.id}-${product.orderId}`}
                      className={`rv-product-card ${isSelected ? "selected" : ""}`}
                      onClick={() => handleProductSelect(product)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) =>
                        e.key === "Enter" && handleProductSelect(product)
                      }
                    >
                      <div className="rv-product-img">
                        <img
                          src={product.image || logoImage}
                          alt={product.name}
                          onError={(e) => {
                            e.target.src = logoImage;
                          }}
                        />
                      </div>

                      <div className="rv-product-info">
                        <div className="rv-product-name">{product.name}</div>
                        {product.brand && (
                          <div className="rv-product-meta">{product.brand}</div>
                        )}
                        <div className="rv-product-meta rv-order-no">
                          Order #{product.orderNo}
                        </div>

                        {/* Already reviewed indicator */}
                        {prevCount > 0 && (
                          <div className="rv-already-reviewed-tag">
                            <MyReviewBadgeIcon />
                            Reviewed {prevCount} time{prevCount > 1 ? "s" : ""}
                          </div>
                        )}
                      </div>

                      {isSelected && (
                        <div className="rv-selected-badge">
                          <CheckIcon />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Rating + Comment + Images — only after selecting a product */}
          {selectedProduct && (
            <>
              <div className="rv-field">
                <label className="rv-label">Your Rating</label>
                <StarRatingInput
                  value={newReview.rating}
                  onChange={(v) =>
                    setNewReview((prev) => ({ ...prev, rating: v }))
                  }
                />
              </div>

              <div className="rv-field">
                <label className="rv-label">Your Review</label>
                <textarea
                  className="rv-textarea"
                  rows={4}
                  placeholder="Share your experience with this product…"
                  value={newReview.comment}
                  onChange={(e) =>
                    setNewReview((prev) => ({
                      ...prev,
                      comment: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="rv-field">
                <label className="rv-label">
                  Add Photos <span className="rv-optional">(optional)</span>
                </label>
                <label className="rv-file-label" htmlFor="rv-images">
                  <ImageIcon />
                  <span>
                    {newReview.review_images.length > 0
                      ? `${newReview.review_images.length} image${newReview.review_images.length !== 1 ? "s" : ""} selected`
                      : "Choose up to 5 images"}
                  </span>
                </label>
                <input
                  id="rv-images"
                  type="file"
                  multiple
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) =>
                    setNewReview((prev) => ({
                      ...prev,
                      review_images: Array.from(e.target.files || []).slice(
                        0,
                        5,
                      ),
                    }))
                  }
                />
              </div>

              <div className="rv-form-actions">
                <button
                  className="acc-btn"
                  onClick={handleSubmitReview}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Spinner size={12} />
                      &nbsp;Submitting…
                    </>
                  ) : (
                    "Submit Review"
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Reviews List ── */}
      {loading ? (
        <div className="acc-box rv-loading-box">
          <Spinner size={28} />
          <span>Loading your reviews…</span>
        </div>
      ) : reviews.length === 0 ? (
        <div className="acc-box rv-empty-box">
          <div className="rv-empty-icon">
            <svg
              width="44"
              height="44"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#d5c9bf"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          </div>
          <div className="rv-empty-title">No reviews yet</div>
          <div className="rv-empty-sub">
            Your product reviews will appear here. Share your experience after
            delivery!
          </div>
        </div>
      ) : (
        <div className="rv-list">
          {reviews.map((review, i) => (
            <div
              key={review.id || i}
              className="rv-card"
              style={{ animationDelay: `${i * 45}ms` }}
            >
              {/* Product info row */}
              <div className="rv-card-product">
                <div className="rv-card-img">
                  <img
                    src={review.product_image || logoImage}
                    alt={review.product_name}
                    onError={(e) => {
                      e.target.src = logoImage;
                    }}
                  />
                </div>

                <div className="rv-card-product-info">
                  <div className="rv-card-product-name">
                    {review.product_name}
                  </div>

                  {/* "Your Review" badge — since all reviews here are the user's own */}
                  <div className="rv-my-review-badge">
                    <MyReviewBadgeIcon />
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#0e3864",
                        fontWeight: 500,
                      }}
                    >
                      Your Review
                    </span>
                  </div>

                  {review.brand && (
                    <span
                      className="wl-meta-pill"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      {review.brand}
                    </span>
                  )}
                  <div className="rv-card-date">
                    {formatDate(review.created_at)}
                  </div>
                </div>

                <div className="rv-card-stars">
                  {Array.from({ length: 5 }, (_, idx) => (
                    <StarIcon key={idx} filled={idx < review.rating} />
                  ))}
                </div>
              </div>

              {/* Comment */}
              <p className="rv-card-comment">
                "{review.comment || "No comment provided."}"
              </p>

              {/* Review images */}
              {review.review_images?.length > 0 && (
                <div className="rv-card-images">
                  {review.review_images.map((img, idx) => (
                    <div key={idx} className="rv-card-thumb">
                      <img src={img} alt={`Review ${idx + 1}`} />
                    </div>
                  ))}
                </div>
              )}

              {/* Vendor reply */}
              {review.vendor_reply && (
                <div className="rv-vendor-reply">
                  <div className="rv-vendor-reply-header">
                    <span className="rv-vendor-icon">
                      <ReplyIcon />
                    </span>
                    <span className="rv-vendor-name">
                      {review.vendor_reply.shop_name}
                    </span>
                    {review.vendor_reply.replied_at && (
                      <span className="rv-vendor-date">
                        {formatDate(review.vendor_reply.replied_at)}
                      </span>
                    )}
                  </div>
                  <p className="rv-vendor-text">{review.vendor_reply.reply}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
